/**
 * Odmowa obrazu demonstracyjnego powiedziana zdaniem (ADR-0059).
 *
 * Ekrany mają własne, celowo ogólne komunikaty błędu („nie udało się zapisać").
 * Przy odmowie demo to byłaby dokładnie ta nieodróżnialna od awarii porażka,
 * której demo ma unikać — więc transport ogłasza odmowę zdarzeniem, a ten
 * komponent mówi, CZEMU: „wersja demonstracyjna działa tylko do odczytu".
 *
 * Montowany w każdym bundlu: w wersji na żywo zdarzenie nigdy nie pada, więc
 * komponent niczego nie pokazuje i nie wciąga fikstur.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { DEMO_SNAPSHOT_DATE } from '../api/client'
import { DEMO_REFUSAL_EVENT, DEMO_REFUSAL_REASONS, type DemoRefusalReason } from '../api/demo/events'

/** Ta sama odmowa z kilku zapytań naraz (np. dwa widoki miesiąca) to jeden komunikat. */
const REPEAT_WINDOW_MS = 3000

const MESSAGE_KEYS: Readonly<Record<DemoRefusalReason, string>> = {
  'demo-read-only': 'demo.refusal.readOnly',
  'demo-outside-snapshot': 'demo.refusal.outsideSnapshot',
  'demo-filter-unavailable': 'demo.refusal.filterUnavailable',
  'demo-no-files': 'demo.refusal.noFiles',
  'demo-no-onboarding': 'demo.refusal.noOnboarding',
  'demo-fixture-missing': 'demo.refusal.fixtureMissing',
}

function isReason(value: unknown): value is DemoRefusalReason {
  return typeof value === 'string' && (DEMO_REFUSAL_REASONS as readonly string[]).includes(value)
}

export function DemoNotice() {
  const { t } = useTranslation()
  const toast = useToast()
  const lastShown = useRef(new Map<DemoRefusalReason, number>())

  useEffect(() => {
    const onRefusal = (event: Event) => {
      const reason = (event as CustomEvent<{ reason?: unknown }>).detail?.reason
      if (!isReason(reason)) return
      const now = performance.now()
      const previous = lastShown.current.get(reason)
      if (previous !== undefined && now - previous < REPEAT_WINDOW_MS) return
      lastShown.current.set(reason, now)
      toast.show(
        t(MESSAGE_KEYS[reason], { date: formatDate(DEMO_SNAPSHOT_DATE, 'dot') }),
        { tone: reason === 'demo-fixture-missing' ? 'danger' : 'info' },
      )
    }
    globalThis.addEventListener(DEMO_REFUSAL_EVENT, onRefusal)
    return () => globalThis.removeEventListener(DEMO_REFUSAL_EVENT, onRefusal)
  }, [t, toast])

  return null
}
